import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from '../../utils/auth';
import { Product, ProductFormInputs } from '../../types/admin';
import styles from '../../styles/admin/Dashboard.module.css';
import { collection, getDocs } from 'firebase/firestore';
import { doc, deleteDoc, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import ProductEditor from '../../components/admin/ProductEditor';
import ProductList from '../../components/admin/ProductList';
import Orders from '../../components/admin/Orders';
import Spinner from '../../components/common/Spinner';

const Dashboard: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'products' | 'orders'>(
    'products'
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedItem, setSelectedItem] = useState<Product | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const productsRef = collection(db, 'products');
        const snapshot = await getDocs(productsRef);
        const products = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(products as Product[]);
      } catch (error) {
        setError('Error fetching products');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleAdd = () => {
    setSelectedItem(null);
    setShowForm(true);
  };

  const handleEdit = (item: Product) => {
    setSelectedItem(item);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    setIsDeleteModalOpen(true);
    setSelectedProductId(id);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (selectedProductId) {
        setIsDeleting(true);
        const productsRef = doc(db, 'products', selectedProductId);
        await deleteDoc(productsRef);
        setIsDeleteModalOpen(false);
        setProducts((prev) =>
          prev.filter((item) => item.id !== selectedProductId)
        );
      }
    } catch (error) {
      setError('Error deleting product');
      console.error('Error deleting product:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmit = async (item: ProductFormInputs) => {
    try {
      if (item.id) {
        const productsRef = doc(db, 'products', item.id);
        const { id, ...updateData } = item;
        await updateDoc(productsRef, updateData);
        setProducts((prev) =>
          prev.map((prevItem) =>
            prevItem.id === item.id ? { ...prevItem, ...updateData } : prevItem
          )
        );
      } else {
        const productRef = collection(db, 'products');
        const { id, ...createData } = item;
        const docRef = await addDoc(productRef, createData);
        setProducts((prev) => [...prev, { ...createData, id: docRef.id }]);
      }

      setShowForm(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error saving merchandise:', error);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setSelectedItem(null);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedProductId(null);
  };

  const handleLogout = async () => {
    console.log('Logging out');
    const { error } = await signOut();
    if (!error) {
      navigate('/');
    }
  };

  if (isLoading) {
    return (
      <div className={styles.adminDashboardLoading}>
        <h2>Loading...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.adminDashboardError}>
        <h2>{error}</h2>
      </div>
    );
  }

  return (
    <div className={styles.adminDashboard}>
      <header className={styles.adminDashboardHeader}>
        <h1>Admin Dashboard</h1>
        <button onClick={handleLogout} className={styles.logoutButton}>
          Logout
        </button>
      </header>

      <nav className={styles.adminDashboardNav}>
        <button
          className={`${styles.navButton} ${
            activeSection === 'products' ? styles.active : ''
          }`}
          onClick={() => setActiveSection('products')}
        >
          Products
        </button>
        <button
          className={`${styles.navButton} ${
            activeSection === 'orders' ? styles.active : ''
          }`}
          onClick={() => setActiveSection('orders')}
        >
          Orders
        </button>
        {activeSection === 'products' && (
          <button className={styles.addProductButton} onClick={handleAdd}>
            Add New Product
          </button>
        )}
      </nav>

      {activeSection === 'products' ? (
        <div className={styles.productsSection}>
          {showForm ? (
            <ProductEditor
              product={selectedItem}
              onSave={handleSubmit}
              onCancel={handleCancel}
            />
          ) : (
            <ProductList
              products={products}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          )}
        </div>
      ) : (
        <Orders />
      )}

      {/* Delete Product Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className={styles.deleteModalOverlay} onClick={closeDeleteModal}>
          <div
            className={styles.deleteModal}
            onClick={(e) => e.stopPropagation()}
          >
            <button className={styles.closeModal} onClick={closeDeleteModal}>
              ×
            </button>

            <div className={styles.modalInfo}>
              <p>Are you sure you want to delete this product?</p>

              <div className={styles.modalActions}>
                <button
                  onClick={() => handleDeleteConfirm()}
                  className={styles.deleteButton}
                  title="Delete"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <Spinner size="small" color="white" />
                  ) : (
                    'Delete'
                  )}
                </button>
                <button
                  className={styles.editButton}
                  onClick={closeDeleteModal}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
